import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useState } from 'react';
import 'dayjs/locale/ja';

/** 資料の種別。抽出プロンプトへそのまま渡すため、表示名がそのまま値になる。 */
export const DOC_TYPES = [
  'テレビ番組の収録台本',
  'クイズ番組の問題資料',
  'ニュース記事',
  '企業のIR資料',
  '講演・イベント資料',
  '報告書・調査資料',
  'その他',
] as const;

export interface SourceMeta {
  docType: string;
  title: string;
  /** 基準時点（YYYY-MM-DD）。資料内の「今」「最近」を解釈する基準になる */
  asOf: string;
  summary: string;
}

export const EMPTY_SOURCE_META: SourceMeta = {
  docType: '',
  title: '',
  asOf: '',
  summary: '',
};

/** 必須項目がすべて埋まっているか */
export function isSourceMetaComplete(meta: SourceMeta): boolean {
  return !!(meta.docType && meta.title.trim() && meta.asOf && meta.summary.trim());
}

interface Props {
  value: SourceMeta;
  onChange: (meta: SourceMeta) => void;
  disabled?: boolean;
  helperColor?: string;
}

/**
 * 資料のメタ情報入力フォーム（ドキュメント・動画で共通）。
 *
 * ここで入力された内容は抽出プロンプトに注入され、主張の文脈補完に使われる。
 * とくに基準時点は、資料内の「今」「最近」がいつを指すかを確定させるため必須。
 * これが無いとモデルが実行時の現在日付から年を推測し、誤った年の主張が生まれる。
 */
export default function SourceMetaForm({ value, onChange, disabled = false, helperColor }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const set = (patch: Partial<SourceMeta>) => onChange({ ...value, ...patch });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ color: helperColor }}>
        資料の情報を入力してください。抽出時の文脈補完に使われ、「最近」「今」がいつの話かを
        正しく解釈するために必要です。
      </Typography>

      <TextField
        select
        required
        label="資料の種別"
        value={value.docType}
        onChange={(e) => set({ docType: e.target.value })}
        disabled={disabled}
        fullWidth
        size="small"
      >
        {DOC_TYPES.map((t) => (
          <MenuItem key={t} value={t}>
            {t}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        required
        label="資料の名称"
        placeholder="番組名・イベント名・記事タイトルなど"
        helperText="アイテムのタイトルとして表示されます"
        value={value.title}
        onChange={(e) => set({ title: e.target.value })}
        disabled={disabled}
        fullWidth
        size="small"
      />

      {/* 手打ちしづらいネイティブの日付入力ではなく、カレンダーから選べるようにする */}
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <DatePicker
          label="基準時点"
          value={value.asOf ? dayjs(value.asOf) : null}
          onChange={(d) => set({ asOf: d ? d.format('YYYY-MM-DD') : '' })}
          disabled={disabled}
          format="YYYY/MM/DD"
          maxDate={dayjs()}
          slotProps={{
            textField: {
              required: true,
              size: 'small',
              sx: { maxWidth: 260 },
              helperText: '資料が作成・収録された日。「今」「最近」の解釈基準になります',
              // フィールドのどこをクリックしてもカレンダーが開くようにする
              onClick: () => !disabled && setPickerOpen(true),
            },
          }}
          open={pickerOpen}
          onOpen={() => setPickerOpen(true)}
          onClose={() => setPickerOpen(false)}
        />
      </LocalizationProvider>

      <TextField
        required
        label="資料の概要"
        placeholder="何について書かれた資料か、何を検証したいかを簡潔に"
        value={value.summary}
        onChange={(e) => set({ summary: e.target.value })}
        disabled={disabled}
        fullWidth
        multiline
        rows={2}
        size="small"
      />
    </Box>
  );
}
